class CollectionInfiniteScroll extends HTMLElement {
  connectedCallback() {
    if (this.hasAttribute("disabled") || !("IntersectionObserver" in window)) {
      return;
    }

    this.productList = this.querySelector("product-list");
    this.sentinel = this.querySelector("[data-infinite-scroll-sentinel]");
    this.pagination = this.parentElement?.querySelector(".collection__pagination");
    this.nextLink = this.pagination?.querySelector('[rel="next"][href]');

    if (!this.productList || !this.sentinel || !this.pagination || !this.nextLink) {
      if (!this.nextLink) {
        this.setAttribute("complete", "");
      }

      return;
    }

    this._observer = new IntersectionObserver(this._onIntersection.bind(this), {
      rootMargin: "400px 0px"
    });
    this._observer.observe(this.sentinel);
  }

  disconnectedCallback() {
    this._observer?.disconnect();
    this._abortController?.abort();
  }

  async _onIntersection(entries) {
    if (this._isLoading || !entries.some((entry) => entry.isIntersecting)) {
      return;
    }

    await this._loadNextPage();
  }

  async _loadNextPage() {
    if (!this.nextLink) {
      this._observer?.disconnect();
      this.setAttribute("complete", "");
      return;
    }

    this._isLoading = true;
    this.setAttribute("loading", "");
    this._abortController?.abort();
    this._abortController = new AbortController();

    const nextPageUrl = new URL(this.nextLink.href, window.location.origin);
    nextPageUrl.searchParams.set("section_id", this.getAttribute("section-id"));

    try {
      const response = await fetch(nextPageUrl.toString(), {
        signal: this._abortController.signal
      });

      if (!response.ok) {
        throw new Error(`Infinite scroll request failed with status ${response.status}`);
      }

      const responseDocument = new DOMParser().parseFromString(await response.text(), "text/html");
      const nextSection =
        responseDocument.getElementById(`shopify-section-${this.getAttribute("section-id")}`) ||
        responseDocument.querySelector(".shopify-section");
      const nextProductList = nextSection?.querySelector("collection-infinite-scroll product-list");
      const nextPagination = nextSection?.querySelector(".collection__pagination");

      if (!nextProductList || !nextPagination) {
        throw new Error("Infinite scroll could not find the next product batch.");
      }

      const productFragment = document.createDocumentFragment();

      Array.from(nextProductList.children).forEach((child) => {
        productFragment.appendChild(document.importNode(child, true));
      });

      this.productList.appendChild(productFragment);

      const importedPagination = document.importNode(nextPagination, true);
      this.pagination.replaceWith(importedPagination);
      this.pagination = importedPagination;
      this.nextLink = this.pagination.querySelector('[rel="next"][href]');

      if (this.nextLink) {
        this.removeAttribute("complete");
      } else {
        this._observer?.disconnect();
        this.setAttribute("complete", "");
      }

      if (this.nextLink && this._shouldLoadMore()) {
        requestAnimationFrame(() => this._loadNextPage());
      }
    } catch (error) {
      if (error.name !== "AbortError") {
        this._observer?.disconnect();
        console.error(error);
      }
    } finally {
      this._isLoading = false;
      this.removeAttribute("loading");
    }
  }

  _shouldLoadMore() {
    const sentinelBounds = this.sentinel?.getBoundingClientRect();

    return Boolean(sentinelBounds) && sentinelBounds.top <= window.innerHeight + 400;
  }
}

if (!window.customElements.get("collection-infinite-scroll")) {
  window.customElements.define("collection-infinite-scroll", CollectionInfiniteScroll);
}

class DeskmatCollectionFilter {
  constructor() {
    this.abortController = null;
    this.touchStart = null;
    this.suppressClickUntil = 0;

    this._onClick = this._onClick.bind(this);
    this._onTouchStart = this._onTouchStart.bind(this);
    this._onTouchEnd = this._onTouchEnd.bind(this);
    this._onTouchCancel = this._onTouchCancel.bind(this);

    document.addEventListener("click", this._onClick, true);
    document.addEventListener("touchstart", this._onTouchStart, { capture: true, passive: true });
    document.addEventListener("touchend", this._onTouchEnd, { capture: true, passive: false });
    document.addEventListener("touchcancel", this._onTouchCancel, { capture: true, passive: true });
  }

  _findOption(target) {
    return target?.closest?.(".qk-deskmat-filter__option[href]") || null;
  }

  _onClick(event) {
    const option = this._findOption(event.target);

    if (!option) {
      return;
    }

    event.preventDefault();
    event.stopImmediatePropagation();

    if (Date.now() < this.suppressClickUntil) {
      return;
    }

    this._selectOption(option);
  }

  _onTouchStart(event) {
    const option = this._findOption(event.target);

    if (!option || event.touches.length !== 1) {
      this.touchStart = null;
      return;
    }

    const touch = event.touches[0];
    this.touchStart = {
      option,
      x: touch.clientX,
      y: touch.clientY
    };
  }

  _onTouchEnd(event) {
    const touchStart = this.touchStart;
    this.touchStart = null;

    if (!touchStart || event.changedTouches.length !== 1) {
      return;
    }

    const touch = event.changedTouches[0];
    const movedX = Math.abs(touch.clientX - touchStart.x);
    const movedY = Math.abs(touch.clientY - touchStart.y);
    const option = this._findOption(event.target);

    if (option !== touchStart.option || movedX > 10 || movedY > 10) {
      return;
    }

    if (event.cancelable) {
      event.preventDefault();
    }

    event.stopImmediatePropagation();
    this.suppressClickUntil = Date.now() + 700;
    this._selectOption(option);
  }

  _onTouchCancel() {
    this.touchStart = null;
  }

  async _selectOption(option) {
    const section = option.closest(".shopify-section--main-collection");
    const filter = option.closest(".qk-deskmat-filter");
    const requestedUrl = new URL(option.href, window.location.href);
    requestedUrl.searchParams.delete("section_id");

    if (!section || !filter) {
      window.location.assign(requestedUrl.toString());
      return;
    }

    const currentUrl = new URL(window.location.href);

    if (requestedUrl.pathname === currentUrl.pathname && requestedUrl.search === currentUrl.search) {
      return;
    }

    filter.querySelectorAll(".qk-deskmat-filter__option").forEach((filterOption) => {
      if (filterOption === option) {
        filterOption.setAttribute("aria-current", "page");
      } else {
        filterOption.removeAttribute("aria-current");
      }
    });
    filter.setAttribute("aria-busy", "true");

    this.abortController?.abort();
    const abortController = new AbortController();
    this.abortController = abortController;

    const sectionId = section.id.replace("shopify-section-", "");
    const sectionUrl = new URL(requestedUrl);
    sectionUrl.searchParams.set("section_id", sectionId);
    window.history.replaceState({}, "", requestedUrl.toString());

    try {
      const response = await fetch(sectionUrl.toString(), {
        headers: {
          "X-Requested-With": "XMLHttpRequest"
        },
        signal: abortController.signal
      });

      if (!response.ok) {
        throw new Error(`Deskmat filter request failed with status ${response.status}`);
      }

      const responseDocument = new DOMParser().parseFromString(await response.text(), "text/html");
      const nextSection =
        responseDocument.getElementById(`shopify-section-${sectionId}`) ||
        responseDocument.querySelector(".shopify-section--main-collection");
      const currentCollection = section.querySelector(".container .collection");
      const nextCollection = nextSection?.querySelector(".container .collection");

      if (!currentCollection || !nextCollection) {
        throw new Error("Deskmat filter could not find the updated collection results.");
      }

      currentCollection.replaceWith(document.importNode(nextCollection, true));
    } catch (error) {
      if (error.name !== "AbortError") {
        console.error(error);
        window.location.assign(requestedUrl.toString());
      }
    } finally {
      if (this.abortController === abortController) {
        this.abortController = null;
        filter.removeAttribute("aria-busy");
      }
    }
  }
}

if (document.querySelector(".qk-deskmat-filter")) {
  new DeskmatCollectionFilter();
}
